import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../constants';
import AuthService from '../../../services/AuthService';
import SupportEditEventGroup from './events/SupportEditEventGroup';

const SupportEditEvents = ({ supportId, onUpdate }) => {
    // const { token } = useAuth(); // Incorrect usage, hook doesn't provide token directly
    const [events, setEvents] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Initial Load
    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);

            const [eventsRes, typesRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/supports/${supportId}/events`),
                fetch(`${API_BASE_URL}/api/supports/types`)
            ]);

            if (!eventsRes.ok) throw new Error('Failed to fetch events');

            if (typesRes.ok) {
                const typesData = await typesRes.json();
                setEventTypes(Array.isArray(typesData) ? typesData : []);
            }

            const data = await eventsRes.json();
            const formattedData = Array.isArray(data) ? data : (data.events || []);

            // Ensure variants are numbers
            formattedData.forEach(event => {
                if (event.options) {
                    event.options.forEach(opt => {
                        const v = String(opt.variant);
                        if (v.startsWith('variant_')) {
                            opt.variant = parseInt(v.replace('variant_', ''), 10);
                        } else {
                            opt.variant = parseInt(v, 10) || 1;
                        }
                    });
                }
            });

            setEvents(formattedData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [supportId]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);


    // --- Actions ---

    // Update entire event group
    const handleUpdateGroup = (index, updatedGroup) => {
        const newEvents = [...events];
        newEvents[index] = updatedGroup;
        setEvents(newEvents);
    };

    const handleAddEvent = () => {
        setEvents([...events, {
            event_name: 'New Event',
            event_type: 'Usage',
            options: [{
                id: null,
                option_name: 'Choice 1',
                variant: 1,
                effects: []
            }]
        }]);
    };

    // Add Option to Group
    const handleAddOption = (eventIndex) => {
        const updatedEvents = [...events];
        updatedEvents[eventIndex].options.push({
            id: null,
            option_name: 'New Option',
            variant: 1,
            effects: []
        });
        setEvents(updatedEvents);
    };

    // Clone/Add Variant to Option
    const handleAddVariant = (eventIndex, optionIndex) => {
        const updatedEvents = [...events];
        const baseOption = updatedEvents[eventIndex].options[optionIndex];

        updatedEvents[eventIndex].options.push({
            id: null,
            option_name: baseOption.option_name,
            variant: (typeof baseOption.variant === 'number' ? baseOption.variant : 1) + 1,
            effects: baseOption.effects ? baseOption.effects.map(e => ({ ...e })) : []
        });
        setEvents(updatedEvents);
    };

    // Delete Option
    const handleDeleteOption = async (eventIndex, optionIndex) => {
        const option = events[eventIndex].options[optionIndex];

        if (option.id) {
            if (!window.confirm('Are you sure you want to delete this event option from the database? This cannot be undone.')) return;

            try {
                const token = AuthService.getToken();
                const response = await fetch(`${API_BASE_URL}/api/supports/events/${option.id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error('Failed to delete option');
            } catch (err) {
                alert('Error deleting option: ' + err.message);
                return;
            }
        }

        const updatedEvents = [...events];
        updatedEvents[eventIndex].options.splice(optionIndex, 1);

        // Remove event group if empty? User might want to keep empty group? 
        // Logic before didn't auto-remove group. Keep as is.
        setEvents(updatedEvents);
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            // Flatten for saving
            const flatOptions = [];
            events.forEach(event => {
                event.options.forEach(opt => {
                    flatOptions.push({
                        ...opt,
                        event_name: event.event_name,
                        event_type: event.event_type,
                        variant: `variant_${opt.variant}`, // Convert back to string format
                        effects: opt.effects
                    });
                });
            });

            const token = AuthService.getToken();
            const response = await fetch(`${API_BASE_URL}/api/supports/${supportId}/events/batch_save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(flatOptions)
            });

            if (!response.ok) throw new Error('Failed to save events');

            alert('Events saved successfully!');
            fetchEvents(); // Reload
            if (onUpdate) onUpdate();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading events...</div>;
    if (error) return <div className="text-red-500">Error: {error}</div>;

    return (
        <div className="space-y-8">
            {/* Global Datalists */}
            <datalist id="effectTypeOptions">
                <option value="Max" />
                <option value="None" />
                <option value="bond" />
                <option value="description" />
                <option value="mood" />
                <option value="restore" />
                <option value="stat" />
                <option value="stats" />
                <option value="status" />
            </datalist>

            <datalist id="eventTypeOptions">
                {eventTypes.map((type, idx) => (
                    <option key={idx} value={type} />
                ))}
            </datalist>

            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Event Editor</h3>
                <button onClick={handleAddEvent} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">
                    + Add New Event Group
                </button>
            </div>

            {events.map((group, evtIdx) => (
                <SupportEditEventGroup
                    key={evtIdx}
                    group={group}
                    eventTypes={eventTypes} // Not strictly needed as we use ID ref, but useful if we change implementation
                    onChange={(updatedGroup) => handleUpdateGroup(evtIdx, updatedGroup)}
                    onAddOption={() => handleAddOption(evtIdx)}
                    onAddVariant={(optIdx) => handleAddVariant(evtIdx, optIdx)}
                    onDeleteOption={(optIdx) => handleDeleteOption(evtIdx, optIdx)}
                />
            ))}

            <div className="sticky bottom-0 bg-white p-4 border-t shadow-lg flex justify-end gap-3 z-10">
                <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700 disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save All Changes'}
                </button>
            </div>
        </div>
    );
};

export default SupportEditEvents;
